import {
  buildApplyStageRoutePath,
  useDefineProviderRoute,
  useExperienceType,
  useValidationService,
} from "@versatile-credit-apply/composition"
import {
  RouteLocationNormalized,
  RouteRecordRaw,
  RouteRecordSingleView,
} from "vue-router"
import { StaticProviderKey } from "@versatile-credit-apply/omni-apply-client"
import { useApplicationStore } from "@versatile-credit-apply/sdk"
import { StepperId } from "@/models/stepper/stepper-id"

const providerKey: StaticProviderKey = "snap_standard"
const { defineRoute } = useDefineProviderRoute(providerKey)

const termsRoute = defineRoute({
  path: "/apply/:applyStage/offer/terms",
  inherit: true,
  component: () =>
    import("@/providers/snap_standard/views/SnapFinanceApplyTermsView.vue"),
  meta: {
    step: "disclosures",
    stepper: StepperId.APPLY,
  },
})

const verifyCodeRoute = defineRoute({
  path: "/apply/:applyStage/verify-code",
  component: () =>
    import("@/providers/snap_standard/views/SnapVerifyCodeView.vue"),
  meta: {
    step: "applicant",
    stepper: StepperId.APPLY,
  },
})

const introRoute = defineRoute({
  path: "/apply/full/intro",
  meta: {
    nextPath: "forms/application",
  },
  props: true,
  component: () =>
    import("@/providers/snap_standard/views/SnapLeaseToOwnInfoView.vue"),
})

const offerView = defineRoute({
  path: "/apply/:applyStage/offer-info",
  component: () =>
    import("@/providers/snap_standard/views/SnapLeaseToOwnInfoView.vue"),
  meta: {
    step: "applicant",
    stepper: StepperId.APPLY,
  },
})

export const routes: RouteRecordRaw[] = [
  {
    ...(introRoute as RouteRecordSingleView),
    props: {
      nextPath: "/forms/application",
    },
  },
  defineRoute({
    name: "snap.apply.form.application.done",
    path: "/apply/full/forms/application/done",
    redirect(to) {
      // Route to Verify Code screen only if Full Apply after completing the form
      return buildApplyStageRoutePath(providerKey, "/verify-code", {
        params: to.params,
      })
    },
  }),
  {
    ...(offerView as RouteRecordSingleView),
    props: (to: RouteLocationNormalized) => {
      if (to.params.applyStage === "full") {
        return {
          nextPath: "/offer/terms",
        }
      }
      return {
        nextPath: "/additional-collect/primary/banking",
      }
    },
  },
  defineRoute({
    path: "/apply/:applyStage/responses/prequalified",
    inherit: true,
    component: () =>
      import("@/providers/snap_standard/views/SnapPrequalifiedView.vue"),
  }),
  defineRoute({
    path: "/apply/:applyStage/responses/nooffer",
    inherit: true,
    component: () =>
      import("@/providers/snap_standard/views/SnapNoOfferView.vue"),
  }),
  {
    ...(verifyCodeRoute as RouteRecordSingleView),
    props: (to: RouteLocationNormalized) => {
      if (to.params.applyStage === "full") {
        return {
          nextPath: "/offer/terms",
        }
      }
      return {
        nextPath: "/offer-info",
      }
    },
  },
  defineRoute({
    path: "/apply/:applyStage/responses/approved",
    inherit: true,
    component: () =>
      import("@/providers/snap_standard/views/SnapApprovedView.vue"),
  }),
  defineRoute({
    path: "/apply/:applyStage/responses/declined",
    inherit: true,
    component: () =>
      import("@/providers/snap_standard/views/SnapDeclinedView.vue"),
  }),
  defineRoute({
    path: "/apply/:applyStage/responses/pending_offer",
    redirect: (to) => to.fullPath.replace("_offer", ""),
  }),
  defineRoute({
    path: "/apply/:applyStage/responses/pending",
    component: () =>
      import("@/providers/snap_standard/views/SnapPendingView.vue"),
  }),
  defineRoute({
    path: "/apply/:applyStage/additional-collect/:applicantType/banking",
    component: () =>
      import("@/providers/snap_standard/views/SnapFinanceBankingCollectView.vue"),
    meta: {
      step: "applicant",
      stepper: StepperId.APPLY,
    },
  }),
  defineRoute({
    path: "/apply/prequalify/intro",
    component: () =>
      import("@/providers/snap_standard/views/SnapFinanceCascadePrequalifyIntroView.vue"),
    beforeEnter: async () => {
      const applicationStore = useApplicationStore()
      const validationService = useValidationService()

      // validate app for things like housing status which could
      // be supplied by primary but invalid for snap
      const validationErrors = await validationService.validateApplication([
        providerKey,
      ])

      const primaryApplicant = applicationStore.application?.primary?.ssn
      const mobilePhone = applicationStore.application?.primary?.mobilePhone
      const email = applicationStore.application?.primary?.email
      const experienceType = useExperienceType()

      if (!primaryApplicant || validationErrors !== null) {
        return {
          path: `/providers/snap_standard/${experienceType.value}/apply/prequalify/forms/application`,
        }
        //If mobile phone and email are already present, skip this view and submit
      } else if (mobilePhone && email) {
        return {
          path: `/providers/snap_standard/${experienceType.value}/apply/prequalify/submit`,
        }
      } else {
        return true
      }
    },
  }),
  {
    ...(termsRoute as RouteRecordSingleView),
    props: (to: RouteLocationNormalized) => {
      if (to.params.applyStage === "full") {
        return {
          nextPath: "/submit",
        }
      }
      return {
        nextPath: "/patch",
      }
    },
  },
  defineRoute({
    path: "/apply/:applyStage/patchSuccess",
    inherit: true,
    redirect: (to) => {
      const applicationStore = useApplicationStore()
      //Setting the purchaseAmountCents to zero because the funded amount will update in the portal
      applicationStore.setApplicationValue("purchaseAmountCents", 0)
      return buildApplyStageRoutePath(
        to.params.providerKey as string,
        "/offer/accept",
        { params: to.params }
      )
    },
  }),
]
